import { type QueryClient, useMutation, useQueries, useQuery } from "@tanstack/react-query";

import { normalizeInsightDates } from "../utils/insightDates";
import { get, post } from "./client";
import type { ApiResponse } from "./deals";
import type { ApiError } from "./errors";
import { type MappingReadinessReason, useMappingReadiness } from "./mappingReadiness";

export const AI_INSIGHTS_RAID_TABS = [
  { value: "risks-issues", label: "RAID – Risks & Issues" },
  { value: "assumptions", label: "RAID – Assumptions" },
  { value: "dependencies", label: "RAID – Dependencies" },
] as const;

export type AiInsightsRaidTab = (typeof AI_INSIGHTS_RAID_TABS)[number]["value"];
export type AiInsightType =
  | "raid_risks_issues"
  | "raid_dependencies"
  | "raid_assumptions"
  | "planning"
  | "decisions"
  | "day1_readiness";

/** Every current-deal insight generated as part of the authenticated startup warm-up. */
export const AI_INSIGHT_TYPES: readonly AiInsightType[] = [
  "raid_risks_issues",
  "raid_dependencies",
  "raid_assumptions",
  "planning",
  "decisions",
  "day1_readiness",
];

export interface AiInsightJob {
  jobId: number;
  status: "pending" | "processing" | "completed" | "failed";
  insight?: unknown;
  asAt?: string | null;
  reason?: string | null;
  aiOutputExecutionId?: number | null;
  /** The DealDataVersion this poll response was generated against. */
  dealDataVersionId?: number | null;
}

/** The POST contract only acknowledges a queued or reused job; output is always read by polling. */
export interface AiInsightJobReceipt {
  jobId: number;
  status: AiInsightJob["status"];
  created: boolean;
  /** The DealDataVersion the queued/reused job was requested against. */
  dealDataVersionId?: number | null;
}

export interface AiInsightDevGenerationAttempt {
  jobId: number;
  generationKeyId: number;
  attemptSequence: number;
  status: AiInsightJob["status"];
  insightType: AiInsightType;
  dealId: number;
  dealDataVersionId: number;
  promptVersion: string;
  asAtDate: string;
  personaKey: string;
  createdAt: string;
  completedAt?: string | null;
  error?: string | null;
  aiOutputExecutionId?: number | null;
  insight?: unknown;
}

export interface AiInsightDevGenerationHistory {
  items: AiInsightDevGenerationAttempt[];
  page: number;
  pageSize: number;
  totalCount: number;
}

export interface RequestAiInsightDevJob {
  insightType: AiInsightType;
  dealDataVersionId: number;
  promptVersion: string;
  asAtDate: string;
  personaKey: string;
  force: boolean;
}

export interface AiInsightsResponse {
  pageType: AiInsightType;
  status: "ready";
  message: string;
  asAt?: string | null;
  insight: unknown;
  aiOutputExecutionId?: number | null;
  /** The DealDataVersion the returned insight was generated against. */
  dealDataVersionId?: number | null;
}

/** Thrown when a job/poll response's DealDataVersion no longer matches the version the caller expects. */
export class StaleAiInsightVersionError extends Error {
  constructor() {
    super("AI Insight response no longer matches the current deal data version.");
    this.name = "StaleAiInsightVersionError";
  }
}

const AI_INSIGHTS_QUERY_KEY_PREFIX = ["ai-insight-job"] as const;

/** Refetches active insight jobs and marks inactive insight queries stale. */
export function invalidateAiInsightJobs(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: AI_INSIGHTS_QUERY_KEY_PREFIX });
}

const AI_INSIGHTS_DEV_QUERY_KEY_PREFIX = ["ai-insight-dev-generation"] as const;
const POLL_INTERVAL_MS = 10_000; // AI Insight jobs can take minutes to complete.
const MAX_POLL_TIME_MS = 10 * 60 * 1_000; // 10 minute safety limit per job

const sleep = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));

function failedJobError(message: string | undefined, code?: string | null): ApiError {
  return {
    message: message ?? "AI Insight generation did not complete.",
    status: 409,
    ...(code != null && { code }),
  };
}

export async function requestAiInsightJob(
  insightType: AiInsightType,
  force = false,
  dealDataVersionId?: number | null
): Promise<ApiResponse<AiInsightsResponse>> {
  const versionQuery =
    dealDataVersionId != null ? `&dealDataVersionId=${encodeURIComponent(dealDataVersionId)}` : "";
  const requested = await post<ApiResponse<AiInsightJobReceipt>>(
    `/ai-insights/jobs?insightType=${encodeURIComponent(insightType)}&force=${force}${versionQuery}`
  );
  if (!requested.success || requested.data == null) {
    throw failedJobError(requested.message);
  }
  if (dealDataVersionId == null || requested.data.dealDataVersionId !== dealDataVersionId) {
    throw new StaleAiInsightVersionError();
  }

  let latest: AiInsightJob | undefined;
  let isFirstPoll = true;
  const pollStartTime = Date.now();
  while (latest?.status === "pending" || latest?.status === "processing" || isFirstPoll) {
    // Safety: stop polling after MAX_POLL_TIME_MS to avoid runaway network activity
    if (Date.now() - pollStartTime > MAX_POLL_TIME_MS) {
      throw failedJobError(
        `AI Insight generation did not complete within ${MAX_POLL_TIME_MS / 1_000 / 60} minutes.`
      );
    }
    if (!isFirstPoll) await sleep(POLL_INTERVAL_MS);
    isFirstPoll = false;
    const polled = await get<ApiResponse<AiInsightJob>>(
      `/ai-insights/jobs/${requested.data.jobId}`
    );
    if (!polled.success || polled.data == null) {
      throw failedJobError(polled.message);
    }
    latest = polled.data;
    if (latest.status !== "pending" && latest.status !== "processing") break;
  }

  if (dealDataVersionId == null || latest?.dealDataVersionId !== dealDataVersionId) {
    throw new StaleAiInsightVersionError();
  }

  if (latest?.status !== "completed" || latest.insight == null) {
    throw failedJobError(
      latest?.reason ?? "AI Insight generation did not complete before the polling timeout.",
      latest?.reason
    );
  }

  return {
    success: true,
    data: {
      pageType: insightType,
      status: "ready",
      message: "Insight generated.",
      asAt: normalizeInsightDates(latest.asAt),
      insight: normalizeInsightDates(latest.insight),
      aiOutputExecutionId: latest.aiOutputExecutionId,
      dealDataVersionId: latest.dealDataVersionId,
    },
    timestamp: new Date().toISOString(),
  };
}

/** Queues a development-only job with an explicit immutable generation key. */
export async function requestAiInsightDevJob(
  request: RequestAiInsightDevJob
): Promise<AiInsightJobReceipt> {
  const response = await post<ApiResponse<AiInsightJobReceipt>>(
    "/ai-insights/dev/generations/jobs",
    request
  );
  if (!response.success || response.data == null) {
    throw failedJobError(response.message);
  }
  return response.data;
}

export async function replayAiInsightDevJob(jobId: number): Promise<AiInsightJobReceipt> {
  const response = await post<ApiResponse<AiInsightJobReceipt>>(
    `/ai-insights/dev/generations/${jobId}/replay`
  );
  if (!response.success || response.data == null) {
    throw failedJobError(response.message);
  }
  return response.data;
}

export function useAiInsightDevGenerationHistory(page = 1, pageSize = 10) {
  return useQuery({
    queryKey: [...AI_INSIGHTS_DEV_QUERY_KEY_PREFIX, "history", page, pageSize],
    queryFn: async () => {
      const response = await get<ApiResponse<AiInsightDevGenerationHistory>>(
        `/ai-insights/dev/generations?page=${page}&pageSize=${pageSize}`
      );
      if (!response.success || response.data == null) throw failedJobError(response.message);
      return response.data;
    },
    refetchInterval: (query) =>
      query.state.data?.items.some(
        (item) => item.status === "pending" || item.status === "processing"
      )
        ? POLL_INTERVAL_MS
        : false,
  });
}

export function useAiInsightDevGenerationAttempt(jobId: number | null) {
  return useQuery({
    queryKey: [...AI_INSIGHTS_DEV_QUERY_KEY_PREFIX, jobId],
    queryFn: async () => {
      const response = await get<ApiResponse<AiInsightDevGenerationAttempt>>(
        `/ai-insights/dev/generations/${jobId}`
      );
      if (!response.success || response.data == null) throw failedJobError(response.message);
      return response.data;
    },
    enabled: jobId != null,
  });
}

/**
 * `dealDataVersionId` is folded into the query key so a mapping change that
 * produces a new DealDataVersion never reuses a prior version's cache entry.
 * A response for an older version can only ever populate its own (now
 * inactive) cache entry, never the entry the UI is currently observing.
 */
export function aiInsightJobQueryOptions(
  insightType: AiInsightType,
  dealDataVersionId: number | null | undefined,
  enabled = true
) {
  return {
    queryKey: [...AI_INSIGHTS_QUERY_KEY_PREFIX, insightType, dealDataVersionId ?? null],
    queryFn: () => requestAiInsightJob(insightType, false, dealDataVersionId),
    enabled,
  };
}

/**
 * True only when a live job response belongs to the current readiness version.
 * Responses without a DealDataVersionId (legacy shape) are never treated as
 * current for the live hook; stored/historical previews bypass this hook.
 */
export function isCurrentVersionResponse(
  response: ApiResponse<AiInsightsResponse> | undefined,
  currentDealDataVersionId: number | null | undefined
): boolean {
  const responseVersionId = response?.data?.dealDataVersionId;
  return (
    response != null
    && currentDealDataVersionId != null
    && responseVersionId != null
    && responseVersionId === currentDealDataVersionId
  );
}

/** Requests an insight when its panel becomes visible, or reuses the startup warm-up query. */
export function useAiInsightJob(insightType: AiInsightType, enabled = true) {
  const readiness = useMappingReadiness(enabled);
  const dealDataVersionId = readiness.data?.dealDataVersionId ?? null;
  const options = aiInsightJobQueryOptions(insightType, dealDataVersionId, enabled);
  const query = useQuery({
    ...options,
    // Readiness is a business-state gate, not a failed HTTP request. Keep the
    // query disabled until the shared readiness decision is known, ready, and
    // associated with a current data version.
    enabled:
      enabled &&
      readiness.isSuccess &&
      readiness.data?.insights.eligible === true &&
      dealDataVersionId != null,
  });
  const mappingReason: MappingReadinessReason | null =
    enabled && readiness.isSuccess ? (readiness.data?.mappingReason ?? null) : null;
  // Render invariant: never expose a completed insight after readiness has
  // advanced to a different Deal Data Version.
  const data = isCurrentVersionResponse(query.data, dealDataVersionId) ? query.data : undefined;
  return {
    ...query,
    data,
    mappingReason,
    isMappingRequired: enabled && mappingReason === "mapping_required",
  };
}

/** Starts or joins all current-deal insight jobs using the same cache entries as the individual panels. */
export function useAiInsightsStartupJobs(enabled = true) {
  const readiness = useMappingReadiness(enabled);
  const dealDataVersionId = readiness.data?.dealDataVersionId ?? null;
  return useQueries({
    queries: AI_INSIGHT_TYPES.map((insightType) => {
      const options = aiInsightJobQueryOptions(insightType, dealDataVersionId, enabled);
      return {
        ...options,
        enabled:
          enabled &&
          readiness.isSuccess &&
          readiness.data?.insights.eligible === true &&
          dealDataVersionId != null,
      };
    }),
  });
}

/** Deliberately exported for the AI Insight test/settings surface only. */
export function useForceAiInsightJob() {
  const readiness = useMappingReadiness();
  const dealDataVersionId = readiness.data?.dealDataVersionId ?? null;
  return useMutation({
    mutationFn: (insightType: AiInsightType) =>
      requestAiInsightJob(insightType, true, dealDataVersionId),
  });
}

export const insightTypeForRaid = (tab: AiInsightsRaidTab): AiInsightType =>
  (
    ({
      "risks-issues": "raid_risks_issues",
      dependencies: "raid_dependencies",
      assumptions: "raid_assumptions",
    }) satisfies Record<AiInsightsRaidTab, AiInsightType>
  )[tab];

export function getAiInsightsAvailabilityMessage(
  reason: string | undefined,
  insightLabel: string
): string | null {
  if (reason === "no_persona") return "No AI Insights persona is assigned to your account yet.";
  if (reason === "no_data") {
    return `No ${insightLabel.toLowerCase()} data is available for this deal, so insights cannot be generated yet.`;
  }
  if (reason === "document_processing") {
    return "Waiting for document processing to complete before generating AI Insights.";
  }
  if (reason === "mapping_required") {
    return "Waiting for mapping validation to complete before generating AI Insights.";
  }
  return null;
}
